import React from "react";
import { Link, useLocation } from "react-router-dom";
import UserSavingHistory from "../../views/UserSavingHistory";
import UserAccountDashboard from "../../views/UserAccountDashboard";
import UserAccountDetails from "../../views/UserAccountDetails";
import UserAccountTargets from "../../views/UserAccountTargets";
import UserAccountPayment from "../../views/UserAccountPayment";
import { useFundingSource } from "../../swr/funding";
import { useSavingsTowardsRent } from "../../swr/saveForRent";
import MySwipeableView from "../../components/MySwipeableView";
import UserPaymentSchedule from "../../views/UserPaymentSchedule";
import { SavingsTowardsRent } from "../../types/Savings";
import UserAccountBookings from "../../views/UserAccountBookings";
import {
  useBookings,
  useUserInspections,
  OUTGOING_BOOKINGS_PAGE_SIZE,
} from "../../swr/inspection";
import UserAccountCreateWallet from "../../views/UserAccountCreateWallet";
import UserAccountWallet from "../../views/UserAccountWallet";
import { getWalletDetails } from "../../swr/wallet";
import ToastContext from "../../contexts/ToastContext";
import { GetWalletDetailsResponseData } from "../../types/Wallet";
import {
  useStateContext,
  StateContextProps,
} from "../../contexts/StateContext";
import { useAuth } from "../../contexts/AuthContext";
import { UserTypeBackEnd } from "../../types/UserType";

const Account: React.FC<any> = () => {
  // Refs
  const sectionRef = React.useRef<HTMLDivElement>(null);

  const { openError } = React.useContext(ToastContext);

  // Fetch funding sources
  let uid = localStorage.getItem("id") as string;
  let username = "";
  const { user, isAuthenticated } = useAuth();
  // userType is stored on the account at registration and returned by
  // /api/Account/me (0 Landlord, 1 Agent, 2 Tenant).
  const isTenant = user?.userType === UserTypeBackEnd.Tenant;
  const { data, isLoading, isError, mutate } = useFundingSource(uid);
  const savingsResult = useSavingsTowardsRent(uid);
  const savingTowardsRent = savingsResult && savingsResult.savings;
  // const reversedSavings = savingTowardsRent && [...savingTowardsRent].reverse();

  const bookingsResult = useBookings(); // incoming

  // Outgoing bookings are paged server-side, so the page lives here alongside
  // the hook that fetches it — changing it refetches that page.
  const [outgoingPage, setOutgoingPage] = React.useState(1); // API is 1-based
  const [outgoingPageSize, setOutgoingPageSize] = React.useState(
    OUTGOING_BOOKINGS_PAGE_SIZE
  );
  const outgoingResult = useUserInspections(
    outgoingPage,
    outgoingPageSize
  );

  // Display
  interface DisplayState {
    dashboard: boolean;
    accountDetails: boolean;
    billing: boolean;
    orders: boolean;
    payment: boolean;
    wallet: boolean;
    bookings: boolean;
  }

  // Initial State
  const initialState: DisplayState = {
    dashboard: false,
    accountDetails: false,
    billing: false,
    orders: true,
    payment: false,
    wallet: false,
    bookings: false,
  };

  // Location
  const location = useLocation();

  // Scroll
  const executeScroll = () => {
    window.scrollTo(0, 300);
  };

  // State
  const [displayState, setDisplayState] =
    React.useState<DisplayState>(initialState);
  const [planIndex, setPlanIndex] = React.useState<number>(0);
  const [plan, setPlan] = React.useState<SavingsTowardsRent>();
  const [walletDetails, setWalletDetails] =
    React.useState<GetWalletDetailsResponseData>();
  const [detailsLoading, setDetailsLoading] = React.useState<boolean>(true);
  const [email, setEmail] = React.useState<string | undefined>("");
  const [hasWallet, setHasWallet] = React.useState(true);

  const { currentBalance, setCurrentBalance }: StateContextProps =
    useStateContext();

  // Handlers
  const handlePaymentUpdate = () => {
    mutate();
  };

  // View Handlers
  const handleDashboardClick = () => {
    setDisplayState({
      dashboard: true,
      accountDetails: false,
      billing: false,
      orders: false,
      payment: false,
      wallet: false,
      bookings: false,
    });
  };
  const handleAccountClick = () => {
    setDisplayState({
      dashboard: false,
      accountDetails: true,
      billing: false,
      orders: false,
      payment: false,
      wallet: false,
      bookings: false,
    });
  };
  const handleBillingClick = () => {
    setDisplayState({
      dashboard: false,
      accountDetails: false,
      billing: true,
      orders: false,
      payment: false,
      wallet: false,
      bookings: false,
    });
  };
  const handleOrdersClick = () => {
    setDisplayState({
      dashboard: false,
      accountDetails: false,
      billing: false,
      orders: true,
      payment: false,
      wallet: false,
      bookings: false,
    });
  };
  const handlePaymentClick = () => {
    setDisplayState({
      dashboard: false,
      accountDetails: false,
      billing: false,
      orders: false,
      payment: true,
      wallet: false,
      bookings: false,
    });
  };
  const handleWalletClick = () => {
    setDisplayState({
      dashboard: false,
      accountDetails: false,
      billing: false,
      orders: false,
      payment: false,
      wallet: true,
      bookings: false,
    });
  };
  const handleBookingsClick = () => {
    setDisplayState({
      dashboard: false,
      accountDetails: false,
      billing: false,
      orders: false,
      payment: false,
      wallet: false,
      bookings: true,
    });
  };

  const handleSavingPlanSelect = (savingPlan?: SavingsTowardsRent) => {
    setPlan(savingPlan);
    if (planIndex === 0) {
      setPlanIndex(1);
    }
  };
  const handleSavingPlanDismiss = () => {
    if (planIndex === 1) {
      setPlanIndex(0);
    }
    setPlan(undefined);
  };

  const returnClassName = () => {};

  //Effects
  React.useEffect(() => {
    switch (location.hash) {
      case "#liton_tab_1_1":
        handleDashboardClick();
        break;

      case "#liton_tab_1_2":
        handleOrdersClick();
        break;

      case "#liton_tab_1_3":
        handleBillingClick();
        break;

      case "#liton_tab_1_4":
        handleAccountClick();
        break;

      case "#liton_tab_1_5":
        handlePaymentClick();
        break;

      case "#liton_tab_1_6":
        handleWalletClick();
        executeScroll();
        break;

      case "#liton_tab_1_7":
        handleBookingsClick();
        break;

      default:
        break;
    }
  }, [location.hash]);

  // Fetch bookings whenever the Bookings tab is opened. The tabs are display
  // toggles on a component that only mounts once, so without this the lists
  // are only ever fetched on the initial page load and clicking the tab makes
  // no request at all — leaving whatever was (or wasn't) loaded then.
  React.useEffect(() => {
    if (!displayState.bookings) return;

    bookingsResult.mutate?.(); // incoming
    outgoingResult.mutate?.(); // outgoing
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayState.bookings]);

  // Surface fetch failures for either list. Without this a failed request
  // just renders an empty table, which is indistinguishable from having no
  // bookings at all.
  React.useEffect(() => {
    if (!displayState.bookings) return;
    if (bookingsResult.isError || outgoingResult.isError) {
      openError("An error occurred fetching your bookings");
    }
  }, [displayState.bookings, bookingsResult.isError, outgoingResult.isError]);

  //On load get wallet details
  React.useEffect(() => {
    if (isAuthenticated) {
      getWalletDetails().then((result) => {
        // console.log(getWalletId(token as string));

        // if (result.data){
        //     setWalletDetails(result.data.response_data);
        //     console.log(result.data.response_data?.available_balance)
        //     setDetailsLoading(false);
        //     if(result.data.response_data?.available_balance){
        //     setCurrentBalance(result.data.response_data?.available_balance)
        //     }
        // }

        if (result.data) {
          setEmail(
            result &&
              result.data &&
              result.data.data &&
              result.data.data.customer_email
          );
        }

        if (result.error) {
          if (
            result.error.response &&
            result.error.response.data &&
            result.error.response?.data.Message === "01"
          ) {
            setHasWallet(false);
          }
          setDetailsLoading(false);
          // openError('An error occured while fetching wallet details');
        }
      });
    }
  }, [isAuthenticated]);

  return (
    <section
      className="liton__wishlist-area pb-70"
      id="homes_acct_section"
      ref={sectionRef}
      style={{
        ...(window.innerWidth <= 768 && {
          width: "25.5em",
          fontWeight: "thin",
        }),
      }}
    >
      <div className="container">
        <div className="row">
          <div className="col-lg-12">
            {/* <!-- PRODUCT TAB AREA START --> */}
            <div className="ltn__product-tab-area">
              <div className="container">
                <div className="row">
                  <div className="col-lg-4">
                    <div className="ltn__tab-menu-list mb-50">
                      <div className="nav">
                        <Link
                          className={
                            location.hash === "#liton_tab_1_1" ||
                            displayState.dashboard
                              ? "active show"
                              : ""
                          }
                          data-bs-toggle="tab"
                          to="#liton_tab_1_1"
                        >
                          Dashboard <i className="fas fa-home"></i>
                        </Link>
                        <Link
                          className={
                            location.hash === "#liton_tab_1_2 " ||
                            displayState.orders
                              ? "active show"
                              : ""
                          }
                          data-bs-toggle="tab"
                          to="#liton_tab_1_2"
                        >
                          Saving Targets <i className="fas fa-bullseye"></i>
                        </Link>
                        <Link
                          className={
                            location.hash === "#liton_tab_1_3"
                              ? "active show"
                              : ""
                          }
                          data-bs-toggle="tab"
                          to="#liton_tab_1_3"
                        >
                          Saving History{" "}
                          <i className="fas fa-map-marker-alt"></i>
                        </Link>
                        {!isTenant && (
                          <Link
                            className={
                              location.hash === "#liton_tab_1_7"
                                ? "active show"
                                : ""
                            }
                            data-bs-toggle="tab"
                            to="#liton_tab_1_7"
                          >
                            Bookings <i className="fas fa-book"></i>
                          </Link>
                        )}
                        <Link
                          className={
                            location.hash === "#liton_tab_1_6" ||
                            displayState.wallet
                              ? `active show`
                              : ""
                          }
                          // className={
                          //   location.hash === "#liton_tab_1_6"
                          //     ? `show ${styles.tabIsActive}`
                          //     : ""
                          // }
                          data-bs-toggle="tab"
                          to="#liton_tab_1_6"
                        >
                          Wallet <i className="fas fa-wallet"></i>
                        </Link>

                        {/* <Link className={location.hash === '#liton_tab_1_5' ? 'active show' : ''} 
                                                   data-bs-toggle="tab" to="#liton_tab_1_5">
                                                        Payment Method <i className="fas fa-credit-card"></i>
                                                </Link> */}
                        <Link
                          className={
                            location.hash === "#liton_tab_1_4"
                              ? "active show"
                              : ""
                          }
                          data-bs-toggle="tab"
                          to="#liton_tab_1_4"
                        >
                          Profile Details <i className="fas fa-user"></i>
                        </Link>
                        {/* <Link data-bs-toggle="tab" to="#liton_tab_1_5" className="">Account Details <i className="fas fa-user"></i></Link> */}
                        {/* <Link to="login.html">Logout <i className="fas fa-sign-out-alt"></i></Link> */}
                      </div>
                    </div>
                  </div>
                  <div className="col-lg-8">
                    <div className="tab-content">
                      {displayState.dashboard && (
                        <UserAccountDashboard
                          userName={username}
                          firstName="firstName"
                          lastName="lastName"
                          phoneNumber="phone"
                          email={email}
                        />
                      )}
                      {/* { displayState.wallet && <UserAccountCreateWallet/> } */}
                      {displayState.wallet &&
                        (hasWallet ? (
                          <UserAccountWallet
                            walletDetails={walletDetails}
                            loading={detailsLoading}
                          />
                        ) : (
                          <UserAccountCreateWallet />
                        ))}
                      {displayState.billing && <UserSavingHistory />}
                      {displayState.accountDetails && (
                        <UserAccountDetails
                          email={""}
                          firstName={""}
                          lastName={""}
                          phoneNumber={""}
                          userName={""}
                        />
                      )}
                      {displayState.bookings && (
                        <UserAccountBookings
                          bookings={bookingsResult.bookings}
                          outgoingBookings={outgoingResult.outgoingBookings}
                          outgoingMeta={outgoingResult.meta}
                          outgoingPage={outgoingPage}
                          outgoingPageSize={outgoingPageSize}
                          outgoingLoading={outgoingResult.isValidating}
                          onOutgoingPageChange={setOutgoingPage}
                          onOutgoingPageSizeChange={(size) => {
                            setOutgoingPageSize(size);
                            setOutgoingPage(1); // page 1 of the new size
                          }}
                        />
                      )}
                      {displayState.orders && (
                        <MySwipeableView currentIndex={planIndex}>
                          {/* <UserPaymentSchedule
                                                        onDismiss={handleSavingPlanDismiss}
                                                        savingPlan={targetData1}
                                                    /> */}
                          <UserAccountTargets
                            onSelect={handleSavingPlanSelect}
                            savingsTowardsRent={
                              savingTowardsRent && savingTowardsRent
                            }
                            loading={savingsResult && savingsResult.isLoading}
                          />
                          <UserPaymentSchedule
                            onDismiss={handleSavingPlanDismiss}
                            savingPlan={plan}
                          />
                        </MySwipeableView>
                      )}
                      {displayState.payment && (
                        <UserAccountPayment
                          sources={data?.data}
                          loading={isLoading}
                          onUpdate={handlePaymentUpdate}
                        />
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {/* <!-- PRODUCT TAB AREA END --> */}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Account;
